/**
 * useAnalytics — wires Firebase Analytics identity + screen tracking.
 *
 * Placed once in AppLayout. Responsibilities:
 * 1. Upgrade web consent to 'granted' when user logs in (accepted ToS)
 * 2. Set Firebase userId when auth state changes
 * 3. Log screen_view on every route change
 * 4. Set user properties for audience segmentation
 */

import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import { useCurrentUser } from "@/shared/contexts/CurrentUserContext"
import { useViewContext } from "@/app/stores/viewContext"
import {
  setAnalyticsUserId,
  setAnalyticsUserProperties,
  logScreenView,
  grantAnalyticsConsent,
} from "@/shared/services/analytics"
import { Capacitor } from "@capacitor/core"

export function useAnalytics() {
  const { user } = useCurrentUser()
  const { pathname } = useLocation()
  const viewId = useViewContext((s) => s.viewId)
  const prevUserIdRef = useRef<string>("")

  // When user logs in → upgrade web consent to 'granted' + set userId
  useEffect(() => {
    if (user.userId && user.userId !== prevUserIdRef.current) {
      prevUserIdRef.current = user.userId
      // User accepted ToS at sign-up → upgrade to full cookie-based tracking
      grantAnalyticsConsent()
      void setAnalyticsUserId(user.userId)
      void setAnalyticsUserProperties({
        platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : "web",
        license: user.license ?? "free",
      })
    }
  }, [user.userId, user.license])

  // Log screen_view on every route change
  useEffect(() => {
    void logScreenView(viewId)
  }, [pathname, viewId])
}
